import { useContext } from "react";
import { Formik, Form, FieldArray, Field } from "formik";
import Input from "../_local/components/Input/index";
import Dropdown from "../_local/components/Dropdown/index";
import ImageUpload from "../_local/components/ImageUpload/index";
import FormWrapper from "../_local/components/FormWrapper/index";
import ResetAndSubmit from "../_local/components/ResetAndSubmit/index";
import * as Yup from "yup";
import TextArea from "../_local/components/TextArea/index";
import { AppContext } from "../_local/state/index";
import { getImageValidationSchema } from "../_local/constants/validationSchemas/index";
import Card from "../_local/components/OnboardingFormCard/index";
import ImageHandler from "./ImageHandler";
import MultiImageUpload from "../_local/components/ImageUpload/MultipleImageUpload";

// Comprehensive validation schema
const getTemplateMediaValidationSchema = (templateMedia) =>
  Yup.object({
    templateMedia: Yup.array().of(
      Yup.object({
        IMAGE_FOR: Yup.string().required("Image For is required"),
        MEDIA: Yup.array()
          .of(Yup.mixed().required("Image is required"))
          .test("min", "At least 1 image is required", function (value) {
            return value && value.length >= 1;
          })
          .test("max", "Too many images", function (value) {
            const { IMAGES_COUNT } = this.parent;
            return value && value.length <= IMAGES_COUNT;
          }),
      })
    ),
  });

function getFilteredMediaSections(data) {
  if (!data || !Array.isArray(data)) {
    return [];
  }

  return data
    .filter((item) => Array.isArray(item.media) && item.media.length > 0)
    .map((item) => ({
      name: item.name,
      media: item.media,
    }));
}

function getMediaSectionsFromTemplate(savedTemplate) {
  if (!savedTemplate || !savedTemplate.WEBSITE_CONTENT_FORMAT) {
    console.warn("No saved template or WEBSITE_CONTENT_FORMAT found");
    return [];
  }

  // Parse the WEBSITE_CONTENT_FORMAT if it's a string
  let contentFormat;
  try {
    contentFormat =
      typeof savedTemplate.WEBSITE_CONTENT_FORMAT === "string"
        ? JSON.parse(savedTemplate.WEBSITE_CONTENT_FORMAT)
        : savedTemplate.WEBSITE_CONTENT_FORMAT;
  } catch (error) {
    console.error("Error parsing WEBSITE_CONTENT_FORMAT:", error);
    return [];
  }

  // Extract media sections from the parsed content format
  return getFilteredMediaSections(contentFormat);
}

const TemplateMedia = ({
  handleNavigate,
  resetFormData,
  disableForm,
}: {
  handleNavigate;
  resetFormData?: any;
  titleAndDesNotRequired?: boolean;
  disableForm?: boolean;
}) => {
  const savedTemplate = JSON.parse(sessionStorage.getItem("template"));

  // Extract required media sections from saved template
  const requiredMedia = getMediaSectionsFromTemplate(savedTemplate);

  const loadInitialData = () => {
    const savedData = JSON.parse(sessionStorage.getItem("templateMedia")) || [];

    if (savedData.length) {
      return savedData.map((item) => ({
        ...item,
        MEDIA: Array.isArray(item.MEDIA) ? item.MEDIA : [],
      }));
    }

    // If no saved data, create initial structure from required media
    return requiredMedia.map((item) => ({
      IMAGE_FOR: item?.name,
      MEDIA: [],
      IMAGES_COUNT: item?.media?.length || 1, // Default to 1 if not specified
    }));
  };


  // If no saved template or no media sections, show error or loading state
  if (!savedTemplate) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <p className="text-red-500">
          No template selected. Please go back and select a template.
        </p>
      </div>
    );
  }

  if (requiredMedia.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <p className="text-gray-500">
          No media sections found in the selected template.
        </p>
      </div>
    );
  }

  return (
    <Formik
      initialValues={{ templateMedia: loadInitialData() }}
      enableReinitialize
      validationSchema={getTemplateMediaValidationSchema(loadInitialData())}
      onSubmit={(values) => {
        const submittedSections = values.templateMedia;
        let originalTemplate;
        try {
          originalTemplate =
            typeof savedTemplate.WEBSITE_CONTENT_FORMAT === "string"
              ? JSON.parse(savedTemplate.WEBSITE_CONTENT_FORMAT)
              : savedTemplate.WEBSITE_CONTENT_FORMAT;
        } catch (error) {
          console.error("Error parsing template content format:", error);
          originalTemplate = [];
        }
        const submittedSectionNames = submittedSections.map(
          (section) => section.IMAGE_FOR
        );

        const mergedTemplate = originalTemplate.map((templateSection) => {
          if (!submittedSectionNames.includes(templateSection.name)) {
            return { ...templateSection };
          }

          const submittedSection = submittedSections.find(
            (section) => section.IMAGE_FOR === templateSection.name
          );

          const submittedImages = submittedSection?.MEDIA || [];
          const originalImages = templateSection.media || [];

          const mergedMedia = originalImages.map((originalImage, index) => {
            console.log("image_url", submittedImages[index]);
            return submittedImages[index]
              ? {
                  ...originalImage,
                  image_url: submittedImages[index]?.selectedImage,
                }
              : originalImage;
          });

          return {
            ...templateSection,
            media: mergedMedia,
          };
        });
        sessionStorage.setItem(
          "templateMedia",
          JSON.stringify(values.templateMedia)
        );
        sessionStorage.setItem(
          "templateMediaPayload",
          JSON.stringify(mergedTemplate)
        );
        handleNavigate();
      }}
    >
      {({
        values,
        handleSubmit,
        resetForm,
        errors,
        setFieldValue,
        isSubmitting,
      }) => (
        <FormWrapper
          title="Showcase Your Institute Campus Excellence"
          description="Upload photos of your institution's vibrant spaces-from classrooms to playgrounds-and let us highlight your institution's environment with stunning visuals."
          // subHeading="Upload images min 1 and max 5"
          childern={
            <Form onSubmit={handleSubmit}>
              <div>
                <div className="rounded-2xl my-1 mx-1">
                  <FieldArray name="templateMedia">
                    {({ push, remove, form }) => {
                      return (
                        <div className="flex flex-col space-y-4 mb-4">
                          {values.templateMedia.map((section, index) => {
                            return (
                              <Card
                                key={`templateMedia-${index}`} // Ensure unique key
                                isError={Boolean(
                                  errors?.templateMedia?.[index]
                                )}
                                index={index}
                                handleOpenDrawer={() =>
                                  form.setFieldValue(
                                    `templateMedia.${index}.DRAWER`,
                                    true
                                  )
                                }
                                handleCloseDrawer={() =>
                                  form.setFieldValue(
                                    `templateMedia.${index}.DRAWER`,
                                    false
                                  )
                                }
                                onDelete={() => {
                                  if (values.templateMedia.length > 1) {
                                    remove(index);
                                  } else {
                                    // Prevent removing the last entry
                                    alert(
                                      "At least one campus info must remain."
                                    );
                                  }
                                }}
                                element={section}
                                children={
                                  <div className="flex flex-wrap gap-x-2 gap-y-4">
                                    <div className="w-full">
                                      <label className="block text-sm font-medium mb-1">
                                        Upload Images (Max{" "}
                                        {section?.IMAGES_COUNT})
                                      </label>
                                      <div className="flex flex-col mt-5">
                                        <Field
                                          name={`templateMedia.${index}.MEDIA`}
                                        >
                                          {({ field, form }) => (
                                            <MultiImageUpload
                                              disable={
                                                disableForm ||
                                                section?.MEDIA?.length >=
                                                  section?.IMAGES_COUNT
                                              }
                                              imageValue={field.value}
                                              onChange={(item) => {
                                                const currentImages =
                                                  Array.isArray(section.MEDIA)
                                                    ? section.MEDIA
                                                    : [];
                                                const newImages = [
                                                  ...currentImages,
                                                  item,
                                                ];
                                                form.setFieldValue(
                                                  `templateMedia.${index}.MEDIA`,
                                                  newImages
                                                );
                                              }}
                                            />
                                          )}
                                        </Field>

                                        <div className="mt-4">
                                          <ImageHandler
                                            disable={disableForm}
                                            mediaElement={section}
                                            imagesCount={section?.IMAGES_COUNT}
                                            setMediaElement={(
                                              updatedImages
                                            ) => {
                                              setFieldValue(
                                                `templateMedia.${index}`,
                                                {
                                                  ...section,
                                                  MEDIA: updatedImages,
                                                }
                                              );
                                            }}
                                          />
                                        </div>

                                        {/* {(index === 0) && <div className="w-full mt-4">
                                      <TextArea
                                        disabled={disableForm}
                                        label="Campus Description?"
                                        name={`campus.${0}.ABOUT_CAMPUS`}
                                        autoGenerate={true}
                                        generate={{
                                          "category": "faculty",
                                          "kwargs": {
                                            "name": campusElement?.IMAGE_FOR,
                                          }
                                        }}
                                      />
                                    </div>} */}
                                      </div>
                                    </div>
                                  </div>
                                }
                              />
                            );
                          })}
                        </div>
                      );
                    }}
                  </FieldArray>
                </div>
              </div>
              {!disableForm && (
                <ResetAndSubmit
                  submitToolTip={
                    !values?.templateMedia?.some(
                      (item) =>
                        Array.isArray(item?.MEDIA) && item?.MEDIA?.length > 0
                    )
                      ? "Please fill in at least one campus info section with details or upload at least one image"
                      : Boolean(errors?.templateMedia)
                      ? "Please fill in all required fields"
                      : ""
                  }
                  isError={
                    !values?.templateMedia?.some(
                      (item) =>
                        Array.isArray(item?.MEDIA) && item?.MEDIA?.length > 0
                    ) || Boolean(errors?.templateMedia)
                  }
                  resetForm={() => {
                    resetForm();
                    sessionStorage.removeItem("templateMedia");
                    resetFormData();
                  }}
                />
              )}
            </Form>
          }
        />
      )}
    </Formik>
  );
};

export default TemplateMedia;
